"use client";

import { useNotifications } from "@novu/notification-center";
import {
	FileChartLine,
	FileQuestion,
	LucideBell,
	LucideBuilding,
	LucideCircleStar,
	LucideGoal,
	LucideHome,
	LucideLayoutDashboard,
	LucideLifeBuoy,
	LucideMessageSquareText,
	LucidePaperclip,
	LucideSend,
} from "lucide-react";
import { DisplaySurveyType } from "posthog-js";
import { usePostHog } from "posthog-js/react";
import type * as React from "react";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
} from "@/components/ui/sidebar";
import { useIsPeerFeedbackEnabled } from "@/lib/api/shared/useIsPeerFeedbackEnabled";
import { showLiveChatNewMessage } from "@/lib/liveChat";
import { useGetCurrentUserProfile } from "@/lib/useGetCurrentUserProfile";
import { m } from "@/paraglide/messages";
import { NavAdminArea } from "./NavAdminArea";
import type { NavAreaItem } from "./NavItems";
import { NavSecondary, type SecondaryNavItem } from "./NavSecondary";
import { NavUser } from "./NavUser";
import { NavUserArea } from "./NavUserArea";
import { OrganizationNav } from "./OrganizationNav";
import { PrimaryNav } from "./PrimaryNav";
import { useOrganization } from "./useOrganization";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
	const profile = useGetCurrentUserProfile();
	const {
		organization,
		uiPermissions,
		canAccessFollowUps,
		isOrganizationAdmin,
	} = useOrganization();

	const posthog = usePostHog();
	const notifications = useNotifications();
	const { isEnabled: isPeerFeedbackEnabled } = useIsPeerFeedbackEnabled(
		organization._id,
	);
	const canEditMembers = uiPermissions.memberships.includes("update");
	const canEditSurveys = isOrganizationAdmin;
	const canEditReports = isOrganizationAdmin;
	const canEditBulkDistributions = isOrganizationAdmin;
	const canEditDeliveryPlans = isOrganizationAdmin;
	const canEditRoles = isOrganizationAdmin;
	const canManageSettings = isOrganizationAdmin;
	const canEditLabels = uiPermissions.labels.includes("update");
	const canEditAttributes = isOrganizationAdmin;
	const canEditPeerFeedbackTemplates =
		isOrganizationAdmin && isPeerFeedbackEnabled;
	const showAdminArea =
		isOrganizationAdmin ||
		canEditMembers ||
		canEditSurveys ||
		canEditDeliveryPlans ||
		canEditRoles ||
		canManageSettings ||
		canEditAttributes ||
		canEditLabels;

	const navSecondary: SecondaryNavItem[] = [
		{
			icon: LucideLifeBuoy,
			title: m.new_green_gadfly_cook(),
			onClick: () => showLiveChatNewMessage(),
		},
		{
			icon: LucideSend,
			title: m.trite_aqua_seal_hint(),
			onClick: () =>
				posthog.surveys.displaySurvey("019b373d-a7fd-0000-503e-b8f3b514e702", {
					ignoreConditions: true,
					ignoreDelay: true,
					displayType: DisplaySurveyType.Popover,
				}),
		},
	];

	const primaryNavUserItems: NavAreaItem[] = [
		{
			title: m.lucky_bald_shrimp_beam(),
			url: `/orga/${organization._id}`,
			icon: LucideHome,
			matchExact: true,
		},
		{
			title: m.bald_direct_florian_amaze(),
			url: `/orga/${organization._id}/notifications`,
			icon: LucideBell,
			count: notifications.unseenCount,
			matchExact: true,
		},
	];

	const navUserAreaItems: NavAreaItem[] = [
		{
			title: m.merry_mushy_cod_view(),
			icon: LucideLayoutDashboard,
			url: `/orga/${organization._id}/reports`,
		},
		{
			title: m.zesty_wild_bullock_blink(),
			icon: LucideMessageSquareText,
			url: "#",
			items: [
				{
					title: m.day_extra_elephant_emerge(),
					url: `/orga/${organization._id}/comments`,
				},
				...(canAccessFollowUps
					? [
							{
								title: m.happy_loved_stork_advise(),
								url: `/orga/${organization._id}/followups`,
							},
						]
					: []),
			],
		},
		...(uiPermissions.sharedContent.includes("read")
			? [
					{
						title: m.wide_safe_larva_inspire(),
						url: `/orga/${organization._id}/shared-content`,
						icon: LucidePaperclip,
						matchExact: true,
					},
				]
			: []),
		...(isPeerFeedbackEnabled
			? [
					{
						title: m.bald_broad_weasel_bask(),
						url: `/orga/${organization._id}/peer-feedback`,
						icon: LucideCircleStar,
					},
				]
			: []),
		...(organization.features?.okr === true
			? [
					{
						title: m.key_helpful_butterfly_breathe(),
						url: `/orga/${organization._id}/okr`,
						icon: LucideGoal,
					},
				]
			: []),
	];

	const navAdminAreaItems: NavAreaItem[] = [
		{
			title: m.full_loud_lion_trip(),
			icon: FileQuestion,
			url: "#",
			defaultExpanded: false,
			items: [
				...(canEditSurveys
					? [
							{
								title: m.vivid_formal_clownfish_arrive(),
								url: `/orga/${organization._id}/surveys`,
							},
						]
					: []),
				...(canEditDeliveryPlans
					? [
							{
								title: m.clear_any_skunk_conquer(),
								url: `/orga/${organization._id}/delivery-plans`,
							},
						]
					: []),
			],
		},
		...(isPeerFeedbackEnabled
			? [
					{
						title: "Peer Feedback",
						icon: LucideCircleStar,
						url: "#",
						defaultExpanded: false,
						items: [
							...(canEditPeerFeedbackTemplates
								? [
										{
											title: m.vexed_royal_jay_mop(),
											url: `/orga/${organization._id}/peer-feedback-templates`,
										},
									]
								: []),
						],
					},
				]
			: []),
		{
			title: m.lost_fluffy_nils_agree(),
			icon: FileChartLine,
			url: "#",
			defaultExpanded: false,
			items: [
				...(canEditReports
					? [
							{
								title: m.super_zesty_bulldog_fear(),
								url: `/orga/${organization._id}/manage-reports`,
							},
						]
					: []),
				...(canEditBulkDistributions
					? [
							{
								title: m.true_nice_tern_trip(),
								url: `/orga/${organization._id}/bulkDistributions`,
							},
						]
					: []),
				...(canEditBulkDistributions
					? [
							{
								title: m.full_orange_poodle_twirl(),
								url: `/orga/${organization._id}/shared-reports`,
							},
						]
					: []),
			],
		},
		{
			title: m.green_house_grizzly_spur(),
			icon: LucideBuilding,
			url: "#",
			defaultExpanded: false,
			items: [
				...(canEditMembers
					? [
							{
								title: m.wild_civil_gibbon_gleam(),
								url: `/orga/${organization._id}/memberships`,
							},
						]
					: []),
				...(canEditRoles
					? [
							{
								title: m.hour_jumpy_cowfish_assure(),
								url: `/orga/${organization._id}/membership-roles`,
							},
						]
					: []),
				...(canEditAttributes
					? [
							{
								title: m.brave_smart_mantis_bubble(),
								url: `/orga/${organization._id}/attributes`,
							},
						]
					: []),
				...(canEditLabels
					? [
							{
								title: m.factual_sweet_swallow_laugh(),
								url: `/orga/${organization._id}/labels`,
							},
						]
					: []),
				...(canManageSettings
					? [
							{
								title: m.sleek_front_mare_honor(),
								url: `/orga/${organization._id}/settings`,
							},
						]
					: []),
			],
		},
	];

	return (
		<Sidebar {...props}>
			<SidebarHeader>
				<OrganizationNav />
			</SidebarHeader>
			<SidebarContent>
				<PrimaryNav items={primaryNavUserItems} />
				<NavUserArea items={navUserAreaItems} />

				{showAdminArea && <NavAdminArea items={navAdminAreaItems} />}
				<NavSecondary items={navSecondary} className="mt-auto" />
			</SidebarContent>
			<SidebarFooter>
				{profile && (
					<NavUser
						user={{
							avatar: profile.picture,
							email: profile.email ?? "",
							name: profile.name ?? "",
						}}
					/>
				)}
			</SidebarFooter>
		</Sidebar>
	);
}
